import { notifications } from '@mantine/notifications';
import { useMutation, useQuery } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { MRT_ColumnFiltersState } from 'mantine-react-table';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { lotsApi } from '../../lib/apis/lots';

import { FORM_STATUS, MultisectActionDto, useSharedQueries } from '@data-access';

type UseLotsExportParams = {
  filters: MRT_ColumnFiltersState;
  rowSelection: RowSelectionState;
  selectedIds: number[];
};

export function useLotsExport({
  filters,
  rowSelection,
  selectedIds,
}: UseLotsExportParams) {
  const { t } = useTranslation();
  const sharedQueries = useSharedQueries();
  const [computationRequestId, setComputationRequestId] = useState<
    number | null
  >(null);
  const hasDownloadedRef = useRef(false);

  const prepExport = useMutation({
    mutationFn: lotsApi.prepExport,
    onSuccess: (data) => {
      hasDownloadedRef.current = false;
      setComputationRequestId(data.instance_id);
    },
  });

  const downloadExport = useMutation({
    mutationFn: lotsApi.downloadExport,
  });

  const computationRequest = useQuery({
    ...sharedQueries.computationRequest({ id: computationRequestId! }),
    enabled: computationRequestId !== null,
  });

  useEffect(() => {
    if (
      computationRequest.data?.status === FORM_STATUS.FINISHED &&
      !hasDownloadedRef.current &&
      !downloadExport.isPending
    ) {
      hasDownloadedRef.current = true;
      downloadExport.mutate({ id: computationRequest.data.id });
      setComputationRequestId(null);
    } else if (computationRequest.data?.status === FORM_STATUS.ERROR) {
      notifications.show({
        title: t('common.ERROR'),
        message: t('common.error.pageError'),
        color: 'red',
      });
      setComputationRequestId(null);
    }
  }, [computationRequest.data, downloadExport, t]);

  const buildExportPayload = (): MultisectActionDto => {
    const hasSelection = Object.keys(rowSelection).length > 0;

    if (rowSelection.all) {
      return {
        ids: [],
        all: true,
        selectedFilters: filters,
      };
    }

    if (hasSelection) {
      return { ids: selectedIds };
    }

    return {
      ids: [],
      all: true,
      selectedFilters: filters,
    };
  };

  const handleExport = () => {
    prepExport.mutate(buildExportPayload());
  };

  const isExportLoading =
    prepExport.isPending ||
    downloadExport.isPending ||
    computationRequest.data?.status === FORM_STATUS.PENDING;

  return {
    handleExport,
    isExportLoading,
  };
}
